import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { FAQS, CONSULTATION_URL, resolveFaqAnswer, MOQ_DESCRIPTION_DEFAULT } from "@/data/constants";
import { trackConversion } from "@/hooks/useSiteAnalytics";
import useSEO, { productSchema, breadcrumbSchema } from "@/hooks/useSEO";

const ProductDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [moqDescription, setMoqDescription] = useState(MOQ_DESCRIPTION_DEFAULT);

  useSEO({
    title: p ? `${p.name} — FLOWTALIS™ Private Label` : "Product — FLOWTALIS™",
    description: p?.tagline || "Private-label beverage manufacturing by FLOWTALIS™ — from formulation to shipping.",
    canonical: `https://flowtalis.com/products/${slug}`,
    image: p?.hero_image_url || undefined,
    type: "product",
    jsonLd: p ? [
      productSchema({
        name: p.name,
        slug,
        description: p.tagline || p.description,
        image: p.hero_image_url,
        category: p.collection_slug || "Beverage",
      }),
      breadcrumbSchema([
        { label: "Home", href: "/" },
        { label: "Products", href: "/products" },
        { label: p.name, href: `/products/${slug}` },
      ]),
    ] : undefined,
  });

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      trackConversion("product_view", {
        product_slug: slug,
        product_name: r.data?.name || undefined,
        product_category: r.data?.collection_slug || undefined,
      });
    }).catch(() => setNotFound(true));
    api.get("/products").then((r) => {
      if (r.data?.moq_description) setMoqDescription(r.data.moq_description);
    }).catch(() => {});
  }, [slug]);

  if (notFound) return (
    <PublicLayout><div className="ft-container py-32 text-center">
      <h1 className="text-3xl font-bold">Category not found</h1>
      <Link to="/products" className="ft-btn-primary mt-6 inline-flex">Back to products</Link>
    </div></PublicLayout>
  );

  if (!p) return <PublicLayout><div className="ft-container py-32">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="ft-section-dark py-20">
        <div className="ft-container ft-fade-in relative">
          <Link to="/products" className="inline-flex items-center gap-2 text-white/60 hover:text-[var(--ft-gold)] mb-6 text-sm" data-testid="product-back">
            <ArrowLeft size={16} /> All Categories
          </Link>
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-5">Private Label</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-6">{p.name}</h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-2xl">{p.tagline}</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold" data-testid="product-book-cta">
              Book Consultation <ArrowRight size={16} />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-white/25 text-white/90 hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)] transition-all">Talk to Specialist</Link>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="ft-container grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="ft-eyebrow mb-3">Overview</div>
              <p className="text-[var(--ft-charcoal)] leading-relaxed">{p.description}</p>
            </div>

            <div>
              <div className="ft-eyebrow mb-3">Highlights</div>
              <ul className="space-y-3">
                {p.highlights.map((h, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <Check size={18} style={{ color: "var(--ft-blue)" }} className="mt-1 flex-shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="ft-eyebrow mb-3">Available Sizes</div>
              <div className="flex flex-wrap gap-2">
                {p.bottle_options.map((b) => (
                  <span key={b} data-testid={`detail-size-${b.replace(/\s+/g, "-")}`} className="px-4 py-2 rounded-full border border-[var(--ft-border)] text-sm font-medium">{b}</span>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="ft-card-flat">
              <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Minimum Order</div>
              <div className="text-2xl font-bold mt-2">{p.min_order}</div>
            </div>
            <div className="ft-card-flat">
              <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Production Timeline</div>
              <div className="text-2xl font-bold mt-2">{p.timeline}</div>
            </div>
            <div className="ft-card-flat">
              <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)]">Category</div>
              <div className="text-2xl font-bold mt-2">Private Label</div>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-20 bg-[var(--ft-soft-gray)]">
        <div className="ft-container max-w-3xl">
          <div className="ft-eyebrow mb-4">FAQs</div>
          <h2 className="text-3xl font-bold tracking-tight mb-8">Questions about {p.name.replace("Private Label ", "")}</h2>
          <div className="space-y-3">
            {FAQS.slice(0, 4).map((f, i) => (
              <details key={i} className="ft-card group">
                <summary className="cursor-pointer list-none flex justify-between items-center font-semibold">
                  {f.q}<span className="text-[var(--ft-blue)] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-[var(--ft-muted)] leading-relaxed">{resolveFaqAnswer(f.a, moqDescription)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="ft-section-dark py-24 text-center">
        <div className="ft-container max-w-2xl relative">
          <h2 className="text-white text-3xl md:text-4xl font-bold mb-6 tracking-tight">Ready to launch <span className="ft-serif text-[var(--ft-gold)]">{p.name.replace("Private Label ", "")}</span>?</h2>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold mt-4 inline-flex">
            Book Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ProductDetail;
